import { z } from 'zod';
import { amountSchema } from '../common/amount.js';
import { paginationQuerySchema } from '../common/pagination.js';
import { localeSchema } from '../common/primitives.js';
import { branchRefSchema, currencyRefSchema, employeeRefSchema } from '../common/refs.js';
import { depositListItemSchema, depositStateSchema, exportFormatSchema, } from '../deposits/contract.js';
/**
 * Reports (cash-deposits.md section 9, RPT-01..04). Totals are always per
 * currency: JOD and USD are never added into one figure, and deposits are
 * never called income. Reports use the current (edited) values.
 */
export const REPORT_KINDS = [
    'held',
    'delivered',
    'detailed',
    'by-customer',
    'by-employee',
];
export const reportKindSchema = z.enum(REPORT_KINDS);
/** Filters: period, branch, currency, state, employee, customer. */
export const reportFilterSchema = z.object({
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    branchId: z.string().min(1).optional(),
    currencyId: z.string().min(1).optional(),
    /** Ignored by `held` (always IN_CUSTODY); `delivered` accepts DELIVERED or RETURNED. */
    state: depositStateSchema.exclude(['DRAFT']).optional(),
    /** Deposit or delivery employee. */
    employeeId: z.string().min(1).optional(),
    /** Customer name or phone, as depositor or recipient. */
    customer: z.string().trim().max(200).optional(),
});
export const reportQuerySchema = paginationQuerySchema.extend(reportFilterSchema.shape);
/** GET /reports/:kind/export */
export const reportExportQuerySchema = reportFilterSchema.extend({
    format: exportFormatSchema,
    lang: localeSchema.default('ar'),
});
export const currencyTotalSchema = z.object({
    currency: currencyRefSchema,
    count: z.number().int().nonnegative(),
    total: amountSchema,
});
/**
 * GET /reports/held — الأمانات المحتفظ بها: the sum of IN_CUSTODY deposits per
 * currency, overall and per branch. Not paginated (bounded by branches ×
 * currencies).
 */
export const heldReportSchema = z.object({
    totals: z.array(currencyTotalSchema),
    byBranch: z.array(z.object({
        branch: branchRefSchema,
        totals: z.array(currencyTotalSchema),
    })),
});
/**
 * GET /reports/delivered (DELIVERED and RETURNED, by delivery date) and
 * GET /reports/detailed (every deposit in the filter). `data.rows` is one
 * page; `data.totals` covers the whole filter. `meta` is the pagination.
 */
export const depositRowsReportSchema = z.object({
    rows: z.array(depositListItemSchema),
    totals: z.array(currencyTotalSchema),
});
const roleSummarySchema = z.object({
    count: z.number().int().nonnegative(),
    totals: z.array(currencyTotalSchema),
});
/** GET /reports/by-customer — per customer (phone), as depositor and as actual recipient. */
export const customerReportRowSchema = z.object({
    phone: z.string(),
    name: z.string(),
    asDepositor: roleSummarySchema,
    asRecipient: roleSummarySchema,
});
export const customerReportSchema = z.object({ rows: z.array(customerReportRowSchema) });
/**
 * GET /reports/by-employee — deposits received (confirmed), deliveries made
 * (delivered) and returns made (returned), each counted separately.
 */
export const employeeReportRowSchema = z.object({
    employee: employeeRefSchema,
    depositsReceived: roleSummarySchema,
    deliveriesMade: roleSummarySchema,
    returnsMade: roleSummarySchema,
});
export const employeeReportSchema = z.object({ rows: z.array(employeeReportRowSchema) });
//# sourceMappingURL=contract.js.map